import { CURRENCY_OPTIONS } from '@/lib/constants';
import { TAgentService } from '@/models/agents';
import {
  Button,
  Flex,
  NumberInput,
  Select,
  Text,
  Textarea,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import AgentServiceDeliverables from './AgentServiceDeliverables';

export default function AgentServiceEditor({
  service,
  buttonTitle = 'Save',
  onSubmit,
  onDelete,
}: {
  service?: TAgentService;
  buttonTitle?: string;
  onSubmit: (service: TAgentService) => void;
  onDelete?: (service: TAgentService) => void;
}) {
  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      name: '',
      cogs: 0,
      currency: 'EUR',
      description: '',
      deliverables: [] as TAgentService['deliverables'],
      ...service,
    },
    validate: {
      cogs: (value: number) =>
        value < 0 ? 'Amount must be greater than 0' : null,
      name: (value: string) =>
        value.replace(/[^a-z]/gi, '').length < 3
          ? 'Name must have at least 3 letters'
          : null,
      deliverables: (value) =>
        value.length === 1 ? 'Cannot have only 1 deliverable' : null,
    },
  });

  return (
    <>
      <form
        onSubmit={form.onSubmit((values, event) => {
          event?.stopPropagation();
          onSubmit(values as TAgentService);
        })}
      >
        <Flex
          gap={'sm'}
          direction={{ base: 'column', sm: 'row' }}
          wrap={'wrap'}
        >
          <Textarea
            autosize
            minRows={1}
            maxRows={10}
            flex={'1 0 100%'}
            miw={'200px'}
            label={'Name'}
            size="xs"
            required
            placeholder={'Document storage'}
            key={form.key('name')}
            {...form.getInputProps('name')}
          />
          <Textarea
            autosize
            minRows={1}
            maxRows={10}
            flex={'1 0 100%'}
            miw={'200px'}
            label={'Description'}
            size="xs"
            placeholder={'Document storage'}
            key={form.key('description')}
            {...form.getInputProps('description')}
          />

          <NumberInput
            required
            label={`Costs`}
            size="xs"
            placeholder={'199.99'}
            key={form.key('cogs')}
            {...form.getInputProps('cogs')}
          />
          <Select
            label="Currency"
            required
            data={CURRENCY_OPTIONS}
            searchable
            allowDeselect={false}
            size="xs"
            key={form.key('currency')}
            {...form.getInputProps('currency')}
          />
        </Flex>
        <AgentServiceDeliverables
          deliverables={form.getValues().deliverables}
          onChange={(deliverables: TAgentService['deliverables']) => {
            form.setFieldValue('deliverables', deliverables);
          }}
        />
        {form.errors.deliverables && (
          <Text size="xs" c="red" mt={'sm'}>
            {form.errors.deliverables}
          </Text>
        )}
        <Flex justify={'flex-end'} gap={'sm'}>
          {service?.id && (
            <Button
              size="xs"
              variant="subtle"
              mt={'sm'}
              color="red"
              onClick={() => onDelete?.(service)}
            >
              Delete
            </Button>
          )}
          <Button size="xs" mt={'sm'} type="submit">
            {buttonTitle}
          </Button>
        </Flex>
      </form>
    </>
  );
}
